import React, { useEffect } from 'react'; // Import useEffect
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate, // Import useNavigate
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DashboardLayout } from './components/layout'; // Use DashboardLayout
import PresetsPage from './pages/Presets';
import CreatePresetPage from './pages/CreatePreset';
import PresetDetailPage from './pages/PresetDetail';
import PresetEditPage from './pages/PresetEdit';
import PositionsPage from './pages/Positions';
import { LoginPage } from './pages/LoginPage'; // Import the new Login Page
import { useWallet } from './hooks/useWallet'; // Import the wallet hook to check session status
import Dashboard from './pages/Dashboard';
import WalletDemo from './pages/WalletDemo';
import { WalletProvider } from './context/WalletContext';
import { CustomThemeProvider } from './context/ThemeContext'; // Import our new provider
import { GlobalStyles } from './styles/GlobalStyles';
import './App.css';

// A component to handle the redirect logic based on environment
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useWallet();
  if (!isAuthenticated) {
    // In all environments, unauthenticated users are redirected to the login page within the app.
    return <Navigate to="/login" replace />;
  }
  return children;
};

// A new component to handle the main app logic and redirects
const AppLogic: React.FC = () => {
  const { isAuthenticated, isLoading } = useWallet();
  const navigate = useNavigate();

  useEffect(() => {
    // This effect runs when the authentication state changes.
    if (!isLoading && isAuthenticated) {
      // If the user is authenticated and on the login page, redirect to the dashboard.
      if (window.location.pathname === '/login') {
        navigate('/app/dashboard');
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show a loading indicator while the session is being checked.
  if (isLoading) {
    return <div>Loading Session...</div>;
  }

  return (
    <Routes>
      {/* Public Route: Login Page */}
      <Route path="/login" element={<LoginPage />} />

      {/* 
        Authenticated Application Routes
        All routes under `/app` are protected and rendered within the main Layout.
      */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="wallet-demo" element={<WalletDemo />} />
                <Route path="presets" element={<PresetsPage />} />
                <Route path="presets/create" element={<CreatePresetPage />} />
                <Route path="presets/:id" element={<PresetDetailPage />} />
                <Route path="presets/:id/edit" element={<PresetEditPage />} />
                <Route path="positions" element={<PositionsPage />} />
                <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* 
        A top-level catch-all.
        If a user is authenticated, send them to the dashboard.
        Otherwise, they will be caught by the ProtectedRoute and sent to /login.
      */}
      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
};


const App: React.FC = () => {
  return (
    <WalletProvider>
      <CustomThemeProvider>
        <GlobalStyles />
        <Router>
          <AppLogic />
        </Router>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark" // This may need to be dynamic later
        />
      </CustomThemeProvider>
    </WalletProvider>
  );
};

export default App;
